import { Helmet } from "react-helmet-async";
import {
  SEO_DATA,
  alternatesFor,
  canonicalFor,
  pathFor,
  SITE_URL,
  OG_IMAGE_URL,
} from "../../i18n/seoMeta";

// Per-route SEO component. Renders <title>, meta description, canonical,
// hreflang alternates, OG/Twitter and structured data (Person + ProfessionalService).
// Pass the route id (e.g. "home", "service.mentoring") and the current lang.
export const SEO = ({ routeId, lang }) => {
  const meta = SEO_DATA[lang]?.[routeId];
  if (!meta) return null;

  const canonical = canonicalFor(routeId, lang);
  const alts = alternatesFor(routeId);
  const ogLocale = lang === "cs" ? "cs_CZ" : "en_US";

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Martin Kuchař",
    jobTitle: lang === "cs"
      ? "IT konzultant, mentor a OKR Mentor"
      : "IT consultant, mentor and OKR Mentor",
    url: SITE_URL,
    image: OG_IMAGE_URL,
    email: "mailto:pan.martin.kuchar@gmail.com",
    telephone: "+420602461799",
    sameAs: [
      "https://www.linkedin.com/in/martin-kucha%C5%99-29086311/",
    ],
    knowsAbout: [
      "Engineering management",
      "Agile",
      "Scrum",
      "OKR",
      "Change management",
      "IT consulting",
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "IT Impact",
    url: SITE_URL,
    image: OG_IMAGE_URL,
    description: meta.description,
    areaServed: ["Czech Republic", "Slovakia", "European Union"],
    provider: {
      "@type": "Person",
      name: "Martin Kuchař",
    },
    serviceType: lang === "cs"
      ? [
          "Mentoring IT manažerů",
          "Konzultace a audit IT týmu",
          "Agile a Scrum školení",
          "OKR workshop",
          "Change management v IT",
        ]
      : [
          "IT manager mentoring",
          "IT team consulting and audit",
          "Agile and Scrum training",
          "OKR workshop",
          "Change management for IT",
        ],
  };

  // BreadcrumbList — built dynamically based on the route depth so each
  // subpage gets a proper breadcrumb in Google's rich result. Helps with
  // both indexing speed and search result presentation.
  const homeName = lang === "cs" ? "Domů" : "Home";
  const servicesName = SEO_DATA[lang].services.h1;
  let breadcrumbSchema = null;
  if (routeId === "services") {
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: homeName, item: `${SITE_URL}${pathFor("home", lang)}` },
        { "@type": "ListItem", position: 2, name: servicesName, item: canonical },
      ],
    };
  } else if (routeId.startsWith("service.")) {
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: homeName, item: `${SITE_URL}${pathFor("home", lang)}` },
        { "@type": "ListItem", position: 2, name: servicesName, item: `${SITE_URL}${pathFor("services", lang)}` },
        { "@type": "ListItem", position: 3, name: meta.h1, item: canonical },
      ],
    };
  }

  // Organization schema (only on home) — gives Google a clear entity card.
  const organizationSchema = routeId === "home" ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "IT Impact",
    url: SITE_URL,
    logo: `${SITE_URL}/android-chrome-512x512.png`,
    description: meta.description,
    founder: {
      "@type": "Person",
      name: "Martin Kuchař",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: "+420602461799",
      email: "pan.martin.kuchar@gmail.com",
      availableLanguage: ["Czech", "English"],
    },
    sameAs: [
      "https://www.linkedin.com/in/martin-kucha%C5%99-29086311/",
    ],
  } : null;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {meta.keywords && <meta name="keywords" content={meta.keywords} />}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />

      {/* Canonical + hreflang */}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="cs" href={alts.cs} />
      <link rel="alternate" hrefLang="en" href={alts.en} />
      <link rel="alternate" hrefLang="x-default" href={alts.cs} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={ogLocale} />
      <meta
        property="og:locale:alternate"
        content={lang === "cs" ? "en_US" : "cs_CZ"}
      />
      <meta property="og:site_name" content="IT Impact" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE_URL} />
      <meta property="og:image:alt" content="Martin Kuchař — IT Impact" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />

      {/* Structured data */}
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {organizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Re-export helpers used by callers in routing/Header.
export { pathFor };
